/**
 * Concurrency Limiter Utility
 *
 * Provides a high-performance, zero-dependency sliding window mapper that executes
 * an asynchronous function across an array of items with bounded concurrency.
 *
 * Guarantees:
 * - At most `limit` asynchronous operations run concurrently.
 * - The returned array maintains the exact input ordering.
 * - Memory footprint is strictly bounded to `limit` active items.
 */

export async function mapConcurrent<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return []

  const concurrency = Math.max(1, Math.min(limit, items.length))
  const results: R[] = new Array(items.length)
  let nextIndex = 0

  const workers = Array.from({ length: concurrency }, async () => {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++
      results[currentIndex] = await fn(items[currentIndex], currentIndex)
    }
  })

  await Promise.all(workers)
  return results
}
